import * as _ from "lodash";
import * as Commons from "oc-commons/Commons";
import { Node } from "../Node";
import { json, MetadataRow } from "../../global";
import { AbstractProperty } from "./AbstractProperty";
import { NodeContainer } from "../NodeContainer";

export abstract class PNodeListAbstract<N extends Node>
  extends AbstractProperty<Array<N>, Node>
  implements NodeContainer<N>
{
  default: [];
  NodeClass: typeof Node;
  metadataPersistent = false;

  constructor(args) {
    super({
      type: "NodeList",
      default: [],
      ...args,
    });
  }

  toThree<ThreeInstanceType>(instance: any) {
    throw new Error("not intended");
  }

  fromThree<ThreeInstanceType>(instance: any) {
    throw new Error("not intended");
  }

  fromMetadataRow(metadataRow: MetadataRow): this {
    throw new Error("not intended");
  }

  threeToInternalFormat(value) {
    throw new Error("not intended");
  }

  toMetadataRow(metadataRow: MetadataRow): this {
    throw new Error("not intended");
  }

  toJSON(): json {
    throw new Error("not intended");
  }

  fromJSON(json: json): this {
    throw new Error("not intended");
  }

  typeCheck(value: Array<N>) {
    return _.isArray(value) && value.every((v) => v instanceof this.NodeClass);
  }

  abstract createNode(): N;

  add(value: N) {
    const list = this.get() ?? [];
    list.push(value);
    this.set(list);
  }

  remove(value: Node) {
    let list = this.get() ?? [];
    list = list.filter((x) => x !== value);
    this.set(list);
    value.onRemove();
  }

  filterSearch: (key: string) => Array<N> = _createSearchFilterMethod(
    this,
    (list) => list,
    (list, predicate) => list.filter(predicate),
    (x) => Object.values(x.toJSON()).join(",")
  );

  filterKey: (key: string) => Array<N> = _createFindFilterMethod(
    this,
    (list) => list,
    (list, predicate) => list.filter(predicate),
    (x) => x.getKey()
  );

  findKey: (key: string) => N = _createFindFilterMethod(
    this,
    (list) => null,
    (list, predicate) => list.find(predicate),
    (x) => x.getKey()
  );
}

function _createFindFilterMethod<N extends Node, R>(
  self: PNodeListAbstract<N>,
  emptyCase: (list: Array<N>) => R,
  operator: (list: Array<N>, predicate: (Node) => boolean) => R,
  nodeToString: (Node) => string
) {
  return (key: string) => {
    const list = self.get() || [];
    if (Commons.isEmpty(key)) {
      return emptyCase(list);
    }
    return operator(list, (x) => {
      const xk = nodeToString(x);
      return xk != null && xk == key;
    });
  };
}

function _createSearchFilterMethod<N extends Node, R>(
  self: PNodeListAbstract<N>,
  emptyCase: (list: Array<N>) => R,
  operator: (list: Array<N>, predicate: (Node) => boolean) => R,
  nodeToString: (Node) => string
) {
  return (key: string) => {
    const list = self.get() || [];
    if (Commons.isEmpty(key)) {
      return emptyCase(list);
    }
    const lowerKey = key.toLowerCase();
    return operator(list, (x) => {
      const xk = nodeToString(x)?.toLowerCase();
      return xk != null && xk.includes(lowerKey);
    });
  };
}
